import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

const ServicesDashboard = () => {
  const { t } = useTranslation();

  const serviceTypesOrder = [
    "preventivo",
    "correctivo",
    "instalacion",
    "electric_plant",
    "electric_plant_correctivo",
    "electric_plant_instalacion",
    "vehicular_door",
    "vehicular_door_correctivo",
    "vehicular_door_instalacion",
    "escalator",
    "escalator_correctivo",
    "escalator_instalacion",
  ];

  const serviceTypeLabels = {
    preventivo: t("preventivo_ascensor"),
    correctivo: t("correctivo_ascensor"),
    instalacion: t("instalacion_ascensor"),
    electric_plant: t("preventivo_planta_electrica"),
    electric_plant_correctivo: t("correctivo_planta_electrica"),
    electric_plant_instalacion: t("instalacion_planta_electrica"),
    vehicular_door: t("preventivo_puerta_vehicular"),
    vehicular_door_correctivo: t("correctivo_puerta_vehicular"),
    vehicular_door_instalacion: t("instalacion_puerta_vehicular"),
    escalator: t("preventivo_escalera_electrica"),
    escalator_correctivo: t("correctivo_escalera_electrica"),
    escalator_instalacion: t("instalacion_escalera_electrica"),
  };

  const [serviceData, setServiceData] = useState({
    preventivo: 0,
    correctivo: 0,
    instalacion: 0,
    electric_plant: 0,
    electric_plant_correctivo: 0,
    electric_plant_instalacion: 0,
    vehicular_door: 0,
    vehicular_door_correctivo: 0,
    vehicular_door_instalacion: 0,
    escalator: 0,
    escalator_correctivo: 0,
    escalator_instalacion: 0,
  });

  const [timeFilter, setTimeFilter] = useState("all");
  const [availableMonths, setAvailableMonths] = useState([]);

  const fetchAvailableMonths = async () => {
    try {
      const response = await axiosInstance.get("/schedules/light");
      const dates = response.data.map((schedule) => new Date(schedule.start_time));

      const years = [...new Set(dates.map((date) => date.getFullYear()))].sort((a, b) => b - a);

      const monthlyOptionsByYear = dates.reduce((acc, date) => {
        const year = date.getFullYear();
        const month = date.toLocaleString("default", { month: "long" });
        const key = `${year}${date.getMonth().toString().padStart(2, "0")}`;
        acc[key] = {
          value: `${month} ${year}`,
          label: `${month} ${year}`,
          sortKey: key,
        };
        return acc;
      }, {});

      const allOptions = [
        { value: "all", label: t("allTime") },
        ...years.map((year) => ({
          value: `year_${year}`,
          label: `${t("year")} ${year}`,
        })),
        ...Object.values(monthlyOptionsByYear)
          .sort((a, b) => b.sortKey - a.sortKey)
          .map(({ value, label }) => ({ value, label })),
      ];

      setAvailableMonths(allOptions);
    } catch (error) {
      console.error("Error fetching available months:", error);
    }
  };

  const fetchServiceStats = async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/schedules/light?status=completed`;

      if (timeFilter !== "all" && !timeFilter.startsWith("year")) {
        const [month, year] = timeFilter.split(" ");
        url += `&month=${month}&year=${year}`;
      } else if (timeFilter.startsWith("year")) {
        url += `&period=year`;
      }

      const { data } = await axiosInstance.get(url);
      const stats = data.reduce(
        (acc, schedule) => {
          if (schedule.service_type) {
            acc[schedule.service_type] = (acc[schedule.service_type] || 0) + 1;
          }
          return acc;
        },
        {
          preventivo: 0,
          correctivo: 0,
          instalacion: 0,
          electric_plant: 0,
          electric_plant_correctivo: 0,
          electric_plant_instalacion: 0,
          vehicular_door: 0,
          vehicular_door_correctivo: 0,
          vehicular_door_instalacion: 0,
          escalator: 0,
          escalator_correctivo: 0,
          escalator_instalacion: 0,
        }
      );

      setServiceData(stats);
    } catch (error) {
      console.error("Error fetching service stats:", error);
    }
  };

  const chartData = {
    labels: serviceTypesOrder.map((key) => serviceTypeLabels[key]),
    datasets: [
      {
        data: serviceTypesOrder.map((key) => serviceData[key] || 0),
        backgroundColor: [
          "#e74c3c", "#3498db", "#f1c40f",
          "#2ecc71", "#9b59b6", "#e67e22",
          "#1abc9c", "#34495e", "#95a5a6",
          "#FF6F61", "#8D6E63", "#4DD0E1",
        ],
        borderColor: [
          "#c0392b", "#2980b9", "#f39c12",
          "#27ae60", "#8e44ad", "#d35400",
          "#16a085", "#2c3e50", "#7f8c8d",
          "#C2185B", "#5D4037", "#00ACC1",
        ],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 14,
            family: "'Roboto', sans-serif",
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  useEffect(() => {
    fetchAvailableMonths();
  }, [t]);

  useEffect(() => {
    fetchServiceStats();
  }, [timeFilter]);

  return (
    <div
      style={{
        backgroundColor: "#FFF8E1",
        padding: "2rem",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        margin: "2rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <h2
          style={{
            color: "#212121",
            fontFamily: "'Roboto', sans-serif",
            margin: 0,
          }}
        >
          {t("serviceTypeDistribution")}
        </h2>
        <select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            border: "1px solid #FFC107",
            backgroundColor: "white",
            color: "#212121",
            fontSize: "16px",
            cursor: "pointer",
            minWidth: "200px",
          }}
        >
          {availableMonths.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          height: "400px",
          maxWidth: "600px",
          margin: "0 auto",
          padding: "1rem",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        <Pie data={chartData} options={options} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginTop: "2rem",
          textAlign: "center",
        }}
      >
        {serviceTypesOrder.map((key) => (
          <div
            key={key}
            style={{
              backgroundColor: "white",
              padding: "1rem",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
            }}
          >
            <h3 style={{ color: "#212121", marginBottom: "0.5rem" }}>
              {serviceTypeLabels[key] || key}
            </h3>
            <p
              style={{
                color: "#FFC107",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {serviceData[key] || 0}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesDashboard;
